import React, { useState, useEffect, useRef } from "react";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";

/* ─────────── Demo agency + property ─────────── */
const DEMO_AGENCY = {
  agency_name: "Demo Agency",
  agent_name: "John Doe",
  agent_first_name: "John",
  agent_title: "Licensed Agent",
  agent_email: "john@demoagency.com.au",
  agent_phone: "0400 000 000",
  agent_colour: "#1a3f6b",
  landing_page_slug: "demo-agency",
  logo_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/logo_url_1774836185_14b54735.png",
  profile_image_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/profile-image.jpg",
};

const SUBJECT_PROPERTY = {
  address_line: "22 Sevira Avenue",
  suburb_line: "Gledswood Hills NSW 2557",
  bedrooms: 4,
  bathrooms: 3,
  carspaces: 2,
  photo_url:
    "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&q=70",
  baseline_value: 1820000,
  current_value: 1985000,
  twelve_month_change: 9.1,
};

const COMPARABLE_SALES = [
  {
    address: "14 Sevira Avenue",
    photo:
      "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&q=70",
    price: 1925000,
    beds: 4,
    baths: 3,
    cars: 2,
    soldAgo: "3 weeks ago",
  },
  {
    address: "47 Ridgeline Drive",
    photo:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=70",
    price: 2050000,
    beds: 5,
    baths: 3,
    cars: 2,
    soldAgo: "5 weeks ago",
  },
  {
    address: "8 Camden Valley Way",
    photo:
      "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&q=70",
    price: 1880000,
    beds: 4,
    baths: 2,
    cars: 2,
    soldAgo: "6 weeks ago",
  },
];

const MARKET_CHART_POINTS = [
  1620000, 1640000, 1655000, 1665000, 1685000, 1710000, 1740000, 1760000,
  1790000, 1820000, 1860000, 1910000, 1985000,
];

const MONTH_LABELS = [
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
];

const PAST_APPRAISAL_CONTACTS = [
  { name: "Sarah Mitchell", initials: "SM" },
  { name: "David & Mei Chen", initials: "DC" },
  { name: "Tom Webb", initials: "TW" },
  { name: "Priya Sharma", initials: "PS" },
  { name: "Liam O'Brien", initials: "LO" },
  { name: "Anna Kovacs", initials: "AK" },
  { name: "Jordan Lee", initials: "JL" },
  { name: "Hassan Ali", initials: "HA" },
];

/* ─────────── Animations ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes fadeUp {
        0%   { opacity: 0; transform: translateY(8px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        0% { opacity: 0; } 100% { opacity: 1; }
      }
      @keyframes slideInRight {
        0%   { opacity: 0; transform: translateX(24px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInLeft {
        0%   { opacity: 0; transform: translateX(-24px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInDown {
        0%   { opacity: 0; transform: translateY(-16px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes drawLine {
        0% { stroke-dashoffset: 1000; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes pulseRing {
        0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45); }
        50% { box-shadow: 0 0 0 14px rgba(16,185,129,0); }
      }
      @keyframes envelopeFly {
        0%   { transform: translate(0,0) scale(1); opacity: 0; }
        10%  { opacity: 1; }
        100% { transform: translate(var(--ex, 220px), var(--ey, -160px)) scale(0.4); opacity: 0; }
      }
      @keyframes orbit {
        0%   { transform: rotate(0deg) translateX(var(--r, 140px)) rotate(0deg); }
        100% { transform: rotate(360deg) translateX(var(--r, 140px)) rotate(-360deg); }
      }
      @keyframes calendarPop {
        0%, 60% { transform: scale(1); }
        70% { transform: scale(1.35); background: ${BRAND_ACCENT_COLOR}; color: #fff; box-shadow: 0 0 0 8px rgba(16,185,129,0.18); }
        100% { transform: scale(1); background: ${BRAND_ACCENT_COLOR}; color: #fff; }
      }
      @keyframes caret {
        50% { opacity: 0; }
      }
      @keyframes phoneRing {
        0%, 100% { transform: rotate(0deg); }
        15% { transform: rotate(-3deg); }
        30% { transform: rotate(3deg); }
        45% { transform: rotate(-2deg); }
        60% { transform: rotate(2deg); }
      }
      @keyframes glowPulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
      @keyframes screenFade {
        0% { opacity: 0; transform: translateY(8px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes dot {
        0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1); }
      }
      @keyframes notifSlide {
        0% { transform: translateY(-140%); opacity: 0; }
        15% { transform: translateY(0); opacity: 1; }
        85% { transform: translateY(0); opacity: 1; }
        100% { transform: translateY(-140%); opacity: 0; }
      }
      @keyframes shineSweep {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
      @keyframes envelopeFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-6px); }
      }
      @keyframes streamUp {
        0% { transform: translateY(0); opacity: 0; }
        20% { opacity: 1; }
        100% { transform: translateY(-220px); opacity: 0; }
      }
    `}</style>
  );
}

/* ─────────── Format helpers ─────────── */
const fmtMoneyShort = (n) => {
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return "$" + Math.round(n / 1_000) + "K";
  return "$" + n;
};

/* ─────────── Animated counter ─────────── */
function AnimatedValue({
  from,
  to,
  prefix = "$",
  suffix = "",
  durationMs = 1600,
  decimals = 0,
}) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, durationMs]);
  const display =
    decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-AU");
  return (
    <span>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

/* ─────────── Animated chart ─────────── */
function MiniMarketChart({
  points = MARKET_CHART_POINTS,
  width = 260,
  height = 100,
  animKey = 0,
}) {
  const min = Math.min(...points);
  const max = Math.max(...points);
  const pad = 6;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const stepX = w / (points.length - 1);
  const norm = (v) => h - ((v - min) / (max - min)) * h;

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${pad + i * stepX} ${pad + norm(p)}`)
    .join(" ");

  const areaPath =
    `M ${pad} ${pad + h} ` +
    points.map((p, i) => `L ${pad + i * stepX} ${pad + norm(p)}`).join(" ") +
    ` L ${pad + (points.length - 1) * stepX} ${pad + h} Z`;

  return (
    <svg width={width} height={height} style={{ display: "block" }} key={animKey}>
      <defs>
        <linearGradient id={`mc-grad-${animKey}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={BRAND_ACCENT_COLOR} stopOpacity="0.35" />
          <stop offset="100%" stopColor={BRAND_ACCENT_COLOR} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={areaPath}
        fill={`url(#mc-grad-${animKey})`}
        style={{ animation: "fadeIn 1.4s ease both 0.4s", opacity: 0 }}
      />
      <path
        d={path}
        fill="none"
        stroke={BRAND_ACCENT_COLOR}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 1000,
          strokeDashoffset: 1000,
          animation: "drawLine 1.6s ease forwards",
        }}
      />
      {points.map((p, i) =>
        i === points.length - 1 ? (
          <g
            key={i}
            style={{ animation: "fadeIn 0.5s ease both 1.6s", opacity: 0 }}
          >
            <circle
              cx={pad + i * stepX}
              cy={pad + norm(p)}
              r="9"
              fill={BRAND_ACCENT_COLOR}
              opacity="0.15"
            />
            <circle
              cx={pad + i * stepX}
              cy={pad + norm(p)}
              r="4"
              fill={BRAND_ACCENT_COLOR}
            />
          </g>
        ) : null
      )}
    </svg>
  );
}

/* ─────────── Phone frame ─────────── */
function PhoneFrame({ children, width = 300, height = 600, ring = false, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        background: "#0b1220",
        borderRadius: 42,
        padding: 10,
        boxShadow:
          "0 30px 80px rgba(0,0,0,0.45), inset 0 0 0 2px #1f2937, 0 0 0 1px #000",
        position: "relative",
        animation: ring ? "phoneRing 0.9s ease-in-out" : undefined,
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 14,
          left: "50%",
          transform: "translateX(-50%)",
          width: 110,
          height: 22,
          background: "#000",
          borderRadius: 14,
          zIndex: 10,
        }}
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "#f5f6f8",
          borderRadius: 34,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─────────── Envelope ─────────── */
function EnvelopeIcon({ size = 36 }) {
  return (
    <div
      style={{
        width: size,
        height: size * 0.7,
        background: "#fff",
        borderRadius: 4,
        border: `2px solid ${BRAND_ACCENT_COLOR}`,
        position: "relative",
        boxShadow: "0 8px 18px rgba(16,185,129,0.35)",
      }}
    >
      <svg
        width={size}
        height={size * 0.7}
        viewBox={`0 0 ${size} ${size * 0.7}`}
        style={{ position: "absolute", inset: 0 }}
      >
        <path
          d={`M 2 2 L ${size / 2} ${size * 0.45} L ${size - 2} 2`}
          fill="none"
          stroke={BRAND_ACCENT_COLOR}
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

/* ─────────── Phone email centerpiece ─────────── */
const REPORT_PHASES = ["inbox", "opening", "report", "tap_cta", "reply", "agent_notif"];

function PhoneEmailScene({ isMobile }) {
  const [phase, setPhase] = useState("inbox");
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    const order = REPORT_PHASES;
    const timings = {
      inbox: 4000,
      opening: 1700,
      report: 8000,
      tap_cta: 2200,
      reply: 3800,
      agent_notif: 3400,
    };
    let cancelled = false;
    const next = () => {
      if (cancelled) return;
      const i = order.indexOf(phaseRef.current);
      const nextPhase = order[(i + 1) % order.length];
      setPhase(nextPhase);
      const t = setTimeout(next, timings[nextPhase]);
      timerRef.current = t;
    };
    const timerRef = { current: setTimeout(next, timings[phase]) };
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        position: "relative",
        width: isMobile ? 280 : 320,
        height: isMobile ? 580 : 640,
        margin: "0 auto",
      }}
    >
      {phase === "inbox" && (
        <div
          style={{
            position: "absolute",
            top: -10,
            right: -20,
            zIndex: 20,
            animation: "envelopeFloat 2.2s ease-in-out infinite",
          }}
        >
          <EnvelopeIcon size={42} />
        </div>
      )}

      <PhoneFrame
        width={isMobile ? 280 : 320}
        height={isMobile ? 580 : 640}
        ring={phase === "inbox"}
      >
        <div
          style={{
            height: 38,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 22px 0 22px",
            fontSize: 12,
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          <span>9:41</span>
          <span style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 10 }}>
            <span>●●●●</span>
            <span>📶</span>
          </span>
        </div>

        {phase === "inbox" && <InboxScreen />}
        {phase === "opening" && <OpeningScreen />}
        {(phase === "report" || phase === "tap_cta" || phase === "reply") && (
          <ReportScreen highlight={phase} />
        )}
        {phase === "agent_notif" && <AgentNotifScreen />}

        {phase === "reply" && <ReplyComposeOverlay />}
      </PhoneFrame>
    </div>
  );
}

function InboxScreen() {
  return (
    <div style={{ padding: "8px 14px", animation: "screenFade 0.45s ease both" }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: "#0f172a",
          margin: "6px 0 12px 0",
        }}
      >
        Inbox
      </div>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "12px 12px",
          boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
          border: `1.5px solid ${BRAND_ACCENT_COLOR}`,
          position: "relative",
          animation: "slideInDown 0.5s ease both",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: -120,
            width: 120,
            height: "100%",
            background:
              "linear-gradient(90deg, transparent, rgba(16,185,129,0.18), transparent)",
            animation: "shineSweep 1.8s ease-in-out 0.4s 1",
          }}
        />
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <img
            src={DEMO_AGENCY.profile_image_url}
            alt=""
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
              {DEMO_AGENCY.agent_first_name} from {DEMO_AGENCY.agency_name}
            </div>
            <div style={{ fontSize: 10.5, color: "#64748b" }}>9:01 AM</div>
          </div>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: BRAND_BLUE,
            }}
          />
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 3,
          }}
        >
          Your June report: 22 Sevira Ave is now{" "}
          {fmtMoneyShort(SUBJECT_PROPERTY.current_value)}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.45 }}>
          Hi Sarah, here's this month's update on your home: value, comparable sales, and what's moving locally…
        </div>
        <div
          style={{
            display: "inline-block",
            marginTop: 8,
            padding: "2px 8px",
            background: `${BRAND_ACCENT_COLOR}1f`,
            color: BRAND_ACCENT_COLOR,
            borderRadius: 999,
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 0.4,
          }}
        >
          MONTHLY REPORT
        </div>
      </div>

      {[
        { from: "Coles", subj: "Your weekly catalogue", time: "8:42" },
        { from: "Linkedin", subj: "5 new connections", time: "7:55" },
        { from: "Spotify", subj: "Your June Wrapped", time: "Yesterday" },
        { from: "Council", subj: "Rates notice, Q3", time: "Yesterday" },
      ].map((m) => (
        <div
          key={m.from}
          style={{
            marginTop: 8,
            background: "#fff",
            borderRadius: 10,
            padding: "10px 12px",
            opacity: 0.55,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0f172a" }}>
              {m.from}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{m.time}</div>
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{m.subj}</div>
        </div>
      ))}
    </div>
  );
}

function OpeningScreen() {
  return (
    <div
      style={{
        padding: 14,
        animation: "screenFade 0.4s ease both",
        background: "#fff",
        height: "100%",
      }}
    >
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: BRAND_ACCENT_COLOR,
            color: "#fff",
            fontSize: 28,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "envelopeFloat 1s ease-in-out infinite",
          }}
        >
          ✉
        </div>
        <div style={{ marginTop: 14, fontSize: 13, color: "#64748b" }}>
          Opening your report
          <span style={{ display: "inline-flex", gap: 3, marginLeft: 4 }}>
            <span
              style={{
                animation: "dot 1.4s infinite",
                display: "inline-block",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
              }}
            />
            <span
              style={{
                animation: "dot 1.4s infinite 0.2s",
                display: "inline-block",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
              }}
            />
            <span
              style={{
                animation: "dot 1.4s infinite 0.4s",
                display: "inline-block",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
              }}
            />
          </span>
        </div>
      </div>
    </div>
  );
}

function ReportScreen({ highlight }) {
  return (
    <div
      style={{
        background: "#f5f6f8",
        height: "calc(100% - 38px)",
        overflow: "hidden",
        animation: "screenFade 0.5s ease both",
      }}
    >
      <div
        style={{
          background: DEMO_AGENCY.agent_colour,
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <img
          src={DEMO_AGENCY.logo_url}
          alt=""
          style={{ height: 18, filter: "brightness(0) invert(1)" }}
        />
        <div
          style={{
            color: "#fff",
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 0.8,
            marginLeft: "auto",
          }}
        >
          MONTHLY REPORT · JUN
        </div>
      </div>

      <div
        style={{
          padding: "10px 12px",
          overflow: "auto",
          height: "calc(100% - 38px)",
        }}
      >
        <div style={{ animation: "fadeUp 0.5s ease both 0.1s" }}>
          <div
            style={{
              fontSize: 9.5,
              color: "#64748b",
              fontWeight: 700,
              letterSpacing: 0.6,
            }}
          >
            YOUR HOME
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: "#0f172a",
              marginTop: 1,
            }}
          >
            {SUBJECT_PROPERTY.address_line}
          </div>
          <div style={{ fontSize: 10, color: "#64748b" }}>
            {SUBJECT_PROPERTY.suburb_line}
          </div>
        </div>

        <div
          style={{
            marginTop: 8,
            borderRadius: 10,
            overflow: "hidden",
            position: "relative",
            height: 100,
            animation: "fadeUp 0.5s ease both 0.2s",
          }}
        >
          <img
            src={SUBJECT_PROPERTY.photo_url}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 8,
              bottom: 8,
              background: "rgba(0,0,0,0.65)",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: 999,
            }}
          >
            {SUBJECT_PROPERTY.bedrooms} Bed · {SUBJECT_PROPERTY.bathrooms} Bath ·{" "}
            {SUBJECT_PROPERTY.carspaces} Car
          </div>
        </div>

        <div
          style={{
            marginTop: 10,
            background: "#fff",
            borderRadius: 10,
            padding: 12,
            boxShadow: "0 6px 16px rgba(15,23,42,0.06)",
            animation: "fadeUp 0.5s ease both 0.3s",
          }}
        >
          <div
            style={{
              fontSize: 9.5,
              color: "#64748b",
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            ESTIMATED VALUE
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              marginTop: 2,
              letterSpacing: -0.5,
            }}
          >
            <AnimatedValue
              from={SUBJECT_PROPERTY.baseline_value}
              to={SUBJECT_PROPERTY.current_value}
              durationMs={1800}
            />
          </div>
          <div
            style={{
              marginTop: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: `${BRAND_ACCENT_COLOR}1f`,
              color: BRAND_ACCENT_COLOR,
              padding: "2px 9px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
            }}
          >
            ▲ +{SUBJECT_PROPERTY.twelve_month_change}% past 12 months
          </div>

          <div style={{ marginTop: 8 }}>
            <MiniMarketChart width={250} height={80} animKey={1} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 8,
                color: "#94a3b8",
                marginTop: 1,
                padding: "0 4px",
              }}
            >
              <span>{MONTH_LABELS[0]}'25</span>
              <span>{MONTH_LABELS[6]}'25</span>
              <span>{MONTH_LABELS[12]}'26</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, animation: "fadeUp 0.5s ease both 0.45s" }}>
          <div
            style={{
              fontSize: 9.5,
              color: "#64748b",
              fontWeight: 700,
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            RECENT NEARBY SALES
          </div>
          {COMPARABLE_SALES.map((c, i) => (
            <div
              key={c.address}
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 8,
                marginBottom: 6,
                display: "flex",
                gap: 8,
                alignItems: "center",
                boxShadow: "0 4px 10px rgba(15,23,42,0.04)",
                animation: `slideInRight 0.5s ease both ${0.55 + i * 0.18}s`,
              }}
            >
              <img
                src={c.photo}
                alt=""
                style={{
                  width: 52,
                  height: 38,
                  objectFit: "cover",
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.address}
                </div>
                <div style={{ fontSize: 9, color: "#64748b" }}>
                  {c.beds}/{c.baths}/{c.cars} · sold {c.soldAgo}
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: BRAND_ACCENT_COLOR,
                }}
              >
                {fmtMoneyShort(c.price)}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 10,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            animation: "fadeUp 0.5s ease both 1.2s",
          }}
        >
          <button
            type="button"
            style={{
              background: BRAND_ACCENT_COLOR,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "9px 8px",
              fontSize: 11,
              fontWeight: 800,
              cursor: "default",
              animation:
                highlight === "tap_cta" || highlight === "reply"
                  ? "pulseRing 1.6s ease-in-out infinite"
                  : undefined,
              transform: highlight === "tap_cta" ? "scale(1.03)" : "scale(1)",
              transition: "transform 0.25s ease",
            }}
          >
            Request appraisal
          </button>
          <button
            type="button"
            style={{
              background: "#fff",
              color: "#0f172a",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "9px 8px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "default",
            }}
          >
            View full report
          </button>
        </div>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "#fff",
            padding: 8,
            borderRadius: 10,
            border: "1px solid #eef2f7",
            animation: "fadeUp 0.5s ease both 1.4s",
          }}
        >
          <img
            src={DEMO_AGENCY.profile_image_url}
            alt=""
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>
              {DEMO_AGENCY.agent_name}
            </div>
            <div style={{ fontSize: 9, color: "#64748b" }}>
              {DEMO_AGENCY.agent_phone}
            </div>
          </div>
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              color: BRAND_ACCENT_COLOR,
              letterSpacing: 0.5,
            }}
          >
            REPLY ↩
          </span>
        </div>
      </div>
    </div>
  );
}

function ReplyComposeOverlay() {
  const fullText = "Hi John, yes, keen to chat about an appraisal next week.";
  const [typed, setTyped] = useState("");
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 2;
      setTyped(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(t);
    }, 50);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom: 16,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 20px 40px rgba(15,23,42,0.25)",
        border: `1px solid ${BRAND_ACCENT_COLOR}`,
        padding: 12,
        animation: "slideInDown 0.5s ease both",
        zIndex: 30,
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          color: BRAND_ACCENT_COLOR,
          fontWeight: 800,
          letterSpacing: 0.6,
        }}
      >
        REPLY TO {DEMO_AGENCY.agent_first_name.toUpperCase()}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#0f172a",
          marginTop: 6,
          lineHeight: 1.4,
          minHeight: 36,
        }}
      >
        {typed}
        <span
          style={{
            display: "inline-block",
            width: 1,
            height: 12,
            background: BRAND_ACCENT_COLOR,
            verticalAlign: "middle",
            marginLeft: 1,
            animation: "caret 0.9s steps(2) infinite",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 8,
        }}
      >
        <span
          style={{
            background: BRAND_ACCENT_COLOR,
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            padding: "4px 12px",
            borderRadius: 999,
          }}
        >
          Send →
        </span>
      </div>
    </div>
  );
}

function AgentNotifScreen() {
  return (
    <div
      style={{
        height: "calc(100% - 38px)",
        background: "linear-gradient(180deg, #0b1220 0%, #1e293b 100%)",
        position: "relative",
        animation: "screenFade 0.4s ease both",
        padding: 14,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(59,130,246,0.25), transparent 50%), radial-gradient(circle at 70% 80%, rgba(16,185,129,0.18), transparent 50%)",
        }}
      />
      <div style={{ position: "relative", textAlign: "center", marginTop: 30 }}>
        <div
          style={{
            color: "#fff",
            fontSize: 12,
            opacity: 0.7,
            letterSpacing: 0.5,
          }}
        >
          Monday, 1 June
        </div>
        <div
          style={{
            color: "#fff",
            fontSize: 56,
            fontWeight: 200,
            letterSpacing: -2,
            marginTop: -4,
          }}
        >
          9:02
        </div>
      </div>
      <div
        style={{
          position: "relative",
          marginTop: 30,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          borderRadius: 16,
          padding: 12,
          boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
          animation: "notifSlide 2.2s ease both",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: BRAND_COLOR,
              color: "#fff",
              fontSize: 11,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            V
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: 0.3,
            }}
          >
            VENDORLEADS
          </div>
          <div style={{ fontSize: 9, color: "#64748b", marginLeft: "auto" }}>
            now
          </div>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0f172a" }}>
          Sarah Mitchell just opened her June report
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
          22 Sevira Ave · tapped "Request appraisal"
        </div>
      </div>

      <div
        style={{
          position: "relative",
          marginTop: 10,
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderRadius: 16,
          padding: 12,
          boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
          animation: "fadeUp 0.6s ease both 0.6s",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: BRAND_ACCENT_COLOR,
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✉
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: 0.3,
            }}
          >
            MAIL
          </div>
          <div style={{ fontSize: 9, color: "#64748b", marginLeft: "auto" }}>
            1m ago
          </div>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0f172a" }}>
          Sarah Mitchell
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
          Re: Your June report. Hi John, yes, keen to chat…
        </div>
      </div>
    </div>
  );
}

/* ─────────── Monthly flow row ─────────── */
const FLOW_STEPS = ["calendar", "send", "deliver", "open"];

function MonthlyFlow({ isMobile }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timings = [2400, 2600, 2400, 2400];
    const t = setTimeout(
      () => setStep((s) => (s + 1) % FLOW_STEPS.length),
      timings[step]
    );
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
        gap: isMobile ? 14 : 18,
      }}
    >
      <FlowCard
        active={step === 0}
        index={1}
        title="Every month, automatic"
        sub="Once a month, every customer"
      >
        <CalendarVisual isActive={step === 0} />
      </FlowCard>
      <FlowCard
        active={step === 1}
        index={2}
        title="Per-property reports built"
        sub="Each contact's home, their data"
      >
        <SendingVisual isActive={step === 1} />
      </FlowCard>
      <FlowCard
        active={step === 2}
        index={3}
        title="Branded email arrives"
        sub="From you, with your photo"
      >
        <DeliverVisual isActive={step === 2} />
      </FlowCard>
      <FlowCard
        active={step === 3}
        index={4}
        title="They open. You get pinged."
        sub="Open + appraisal click alerts"
      >
        <OpenVisual isActive={step === 3} />
      </FlowCard>
    </div>
  );
}

function FlowCard({ active, index, title, sub, children }) {
  return (
    <div
      style={{
        background: active
          ? "rgba(16,185,129,0.08)"
          : "rgba(255,255,255,0.04)",
        border: `1px solid ${active ? "rgba(16,185,129,0.45)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 16,
        padding: 16,
        transition: "all 0.4s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.1)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: active ? "0 0 0 4px rgba(16,185,129,0.18)" : "none",
            transition: "all 0.4s ease",
          }}
        >
          {index}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff" }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
            {sub}
          </div>
        </div>
      </div>
      <div
        style={{
          height: 140,
          background: "rgba(0,0,0,0.25)",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.05)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function CalendarVisual({ isActive }) {
  const days = Array.from({ length: 30 }, (_, i) => i + 1);
  return (
    <div style={{ padding: 10, color: "#cbd5e1" }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: 1,
          color: BRAND_ACCENT_COLOR,
          marginBottom: 4,
        }}
      >
        JUNE 2026
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
        }}
      >
        {days.map((d) => (
          <div
            key={d}
            style={{
              fontSize: 8.5,
              textAlign: "center",
              padding: "2px 0",
              borderRadius: 4,
              color: d === 1 ? "#fff" : "rgba(203,213,225,0.5)",
              fontWeight: d === 1 ? 800 : 500,
              background: d === 1 ? BRAND_ACCENT_COLOR : "transparent",
              animation:
                d === 1 && isActive ? "calendarPop 1.4s ease forwards" : "none",
            }}
          >
            {d}
          </div>
        ))}
      </div>
    </div>
  );
}

function SendingVisual({ isActive }) {
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 40,
          height: 40,
          background: BRAND_BLUE,
          color: "#fff",
          borderRadius: 10,
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 0 0 6px rgba(59,130,246,0.18)",
          zIndex: 2,
        }}
      >
        ⚙
      </div>
      {isActive &&
        Array.from({ length: 8 }).map((_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const ex = Math.cos(angle) * 80;
          const ey = Math.sin(angle) * 50;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 18,
                height: 13,
                background: "#fff",
                borderRadius: 2,
                border: `1px solid ${BRAND_ACCENT_COLOR}`,
                "--ex": `${ex}px`,
                "--ey": `${ey}px`,
                animation: `envelopeFly 1.6s ease-out ${i * 0.15}s infinite`,
                transformOrigin: "center",
              }}
            />
          );
        })}
    </div>
  );
}

function DeliverVisual({ isActive }) {
  return (
    <div style={{ padding: 10 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 8,
          border: `1.5px solid ${isActive ? BRAND_ACCENT_COLOR : "#e5e7eb"}`,
          animation: isActive ? "slideInDown 0.5s ease both" : "none",
          boxShadow: isActive ? "0 4px 14px rgba(16,185,129,0.2)" : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <img
            src={DEMO_AGENCY.profile_image_url}
            alt=""
            style={{ width: 18, height: 18, borderRadius: "50%" }}
          />
          <div style={{ fontSize: 9, fontWeight: 700, color: "#0f172a" }}>
            {DEMO_AGENCY.agent_first_name}
          </div>
          <div
            style={{ fontSize: 8, color: "#94a3b8", marginLeft: "auto" }}
          >
            9:01
          </div>
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#0f172a" }}>
          Your June report: 22 Sevira Ave
        </div>
        <div style={{ fontSize: 8.5, color: "#64748b", marginTop: 1 }}>
          Hi Sarah, here's this month's update on your home…
        </div>
      </div>
      <div
        style={{
          marginTop: 6,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 6,
          padding: "4px 6px",
          fontSize: 8,
          color: "rgba(255,255,255,0.55)",
          opacity: 0.6,
        }}
      >
        Bank statement · June
      </div>
    </div>
  );
}

function OpenVisual({ isActive }) {
  return (
    <div style={{ padding: 10, position: "relative", height: "100%" }}>
      <div
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 8,
          padding: 8,
          color: "#cbd5e1",
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: BRAND_BLUE,
            letterSpacing: 0.6,
          }}
        >
          LIVE ALERT
        </div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: "#fff",
            marginTop: 2,
          }}
        >
          Sarah Mitchell opened her report
        </div>
        <div style={{ fontSize: 9, color: "rgba(203,213,225,0.7)" }}>
          22 Sevira Ave · 12 sec ago
        </div>
      </div>
      <div
        style={{
          marginTop: 6,
          background: "rgba(16,185,129,0.12)",
          border: "1px solid rgba(16,185,129,0.35)",
          borderRadius: 8,
          padding: 8,
          color: "#cbd5e1",
          animation: isActive ? "slideInRight 0.6s ease both 0.4s" : "none",
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: BRAND_ACCENT_COLOR,
            letterSpacing: 0.6,
          }}
        >
          ✓ APPRAISAL REQUESTED
        </div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: "#fff",
            marginTop: 2,
          }}
        >
          Sarah tapped "Request appraisal"
        </div>
      </div>
    </div>
  );
}

/* ─────────── Orbiting contacts ─────────── */
function ContactOrbit({ isMobile }) {
  const size = isMobile ? 300 : 380;
  const r1 = isMobile ? 80 : 130;
  const r2 = isMobile ? 130 : 180;
  const pillW = isMobile ? 60 : 70;
  const pillH = isMobile ? 26 : 30;

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        margin: "0 auto",
      }}
    >
      {[r1, r2].map((r) => (
        <div
          key={r}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: r * 2,
            height: r * 2,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            border: "1px dashed rgba(255,255,255,0.15)",
          }}
        />
      ))}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 84,
          height: 84,
          borderRadius: "50%",
          background: "#fff",
          padding: 4,
          boxShadow: "0 14px 40px rgba(16,185,129,0.4)",
          zIndex: 5,
        }}
      >
        <img
          src={DEMO_AGENCY.profile_image_url}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -8,
            left: "50%",
            transform: "translateX(-50%)",
            background: BRAND_ACCENT_COLOR,
            color: "#fff",
            fontSize: 9,
            fontWeight: 800,
            padding: "2px 9px",
            borderRadius: 999,
            letterSpacing: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          YOU
        </div>
      </div>

      {PAST_APPRAISAL_CONTACTS.map((c, i) => {
        const ring = i < 4 ? 0 : 1;
        const r = ring === 0 ? r1 : r2;
        const slotsInRing = 4;
        const idxInRing = ring === 0 ? i : i - 4;
        const startDeg =
          (idxInRing / slotsInRing) * 360 + (ring === 1 ? 45 : 0);
        const duration = ring === 0 ? 30 : 45;
        return (
          <div
            key={c.name}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              "--r": `${r}px`,
              animation: `orbit ${duration}s linear infinite`,
              animationDelay: `${-startDeg / (360 / duration)}s`,
              transformOrigin: "0 0",
            }}
          >
            <div
              style={{
                width: pillW,
                height: pillH,
                marginLeft: -pillW / 2,
                marginTop: -pillH / 2,
                borderRadius: 999,
                background: BRAND_COLOR,
                color: "#fff",
                fontWeight: 700,
                fontSize: isMobile ? 10 : 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid rgba(255,255,255,0.15)",
                boxShadow: "0 8px 18px rgba(0,0,0,0.25)",
                padding: "0 6px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {c.name.split(" ")[0]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────── Engagement live feed ─────────── */
function EngagementPanel({ isMobile }) {
  const [seed, setSeed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeed((s) => s + 1), 4000);
    return () => clearInterval(t);
  }, []);

  const rows = [
    {
      name: "Sarah Mitchell",
      addr: "22 Sevira Ave",
      action: "Requested appraisal",
      time: "2m ago",
      hot: true,
    },
    {
      name: "David Chen",
      addr: "8 Camden Valley Way",
      action: "Opened report",
      time: "14m ago",
      hot: false,
    },
    {
      name: "Priya Sharma",
      addr: "47 Ridgeline Dr",
      action: "Viewed comparables",
      time: "1h ago",
      hot: false,
    },
    {
      name: "Tom Webb",
      addr: "12 Heritage Way",
      action: "Opened report (3rd time)",
      time: "3h ago",
      hot: true,
    },
  ];

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: isMobile ? 16 : 22,
        boxShadow: "0 20px 60px rgba(15,23,42,0.12)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: BRAND_BLUE,
              letterSpacing: 1,
            }}
          >
            LIVE FEED
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              color: "#0f172a",
              marginTop: 2,
            }}
          >
            Who's engaging this month
          </div>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            color: BRAND_ACCENT_COLOR,
            background: `${BRAND_ACCENT_COLOR}1f`,
            padding: "4px 10px",
            borderRadius: 999,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: BRAND_ACCENT_COLOR,
              animation: "glowPulse 1.4s ease-in-out infinite",
            }}
          />
          LIVE
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          marginBottom: 14,
        }}
      >
        {[
          { label: "Sent", val: 184, color: BRAND_BLUE },
          { label: "Opened", val: 127, color: BRAND_COLOR },
          { label: "Appraisals", val: 9, color: BRAND_ACCENT_COLOR },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: "#f8fafc",
              borderRadius: 10,
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "#64748b",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {s.label.toUpperCase()}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: s.color,
                marginTop: 1,
              }}
            >
              <AnimatedValue from={0} to={s.val} prefix="" durationMs={1400} />
            </div>
          </div>
        ))}
      </div>

      <div>
        {rows.map((r, i) => (
          <div
            key={`${r.name}-${seed}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 0",
              borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
              animation: `slideInLeft 0.5s ease both ${i * 0.15}s`,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: r.hot ? `${BRAND_ACCENT_COLOR}1f` : "#f1f5f9",
                color: r.hot ? BRAND_ACCENT_COLOR : "#64748b",
                fontSize: 12,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {r.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                {r.name}{" "}
                <span style={{ color: "#64748b", fontWeight: 500 }}>
                  · {r.addr}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: r.hot ? BRAND_ACCENT_COLOR : "#64748b",
                  fontWeight: r.hot ? 700 : 500,
                }}
              >
                {r.action}
              </div>
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#94a3b8",
                whiteSpace: "nowrap",
              }}
            >
              {r.time}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Section heading ─────────── */
function SectionHeading({
  kicker,
  title,
  sub,
  light = false,
  align = "left",
}) {
  return (
    <div style={{ textAlign: align, marginBottom: 32 }}>
      <div
        style={{
          display: "inline-block",
          background: light
            ? "rgba(16,185,129,0.18)"
            : "rgba(16,185,129,0.1)",
          color: BRAND_ACCENT_COLOR,
          fontSize: "0.78rem",
          fontWeight: 700,
          borderRadius: 6,
          padding: "0.3rem 0.75rem",
          marginBottom: "1rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {kicker}
      </div>
      <h2
        style={{
          fontSize: "clamp(1.7rem, 4vw, 2.4rem)",
          fontWeight: 700,
          color: light ? "#fff" : BRAND_COLOR,
          lineHeight: 1.2,
          margin: 0,
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </h2>
      {sub && (
        <p
          style={{
            fontSize: 16,
            color: light ? "rgba(255,255,255,0.7)" : GRAY_DARK_COLOR,
            lineHeight: 1.6,
            maxWidth: 640,
            margin: align === "center" ? "14px auto 0" : "14px 0 0 0",
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

/* ─────────── Hero ─────────── */
function Hero({ isMobile, onStartTrial, onContact }) {
  return (
    <section
      style={{
        background: `linear-gradient(135deg, ${BRAND_COLOR} 0%, #0f2540 60%, #0a1a2e 100%)`,
        color: "#fff",
        padding: isMobile ? "100px 20px 60px" : "120px 40px 80px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "-20%",
          right: "-10%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(16,185,129,0.18), transparent 65%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.18), transparent 65%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.05fr 1fr",
          gap: isMobile ? 40 : 60,
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(16,185,129,0.15)",
              border: "1px solid rgba(16,185,129,0.35)",
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              color: BRAND_ACCENT_COLOR,
              letterSpacing: 1,
              marginBottom: 22,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
                animation: "glowPulse 1.4s ease-in-out infinite",
              }}
            />
            MONTHLY · AUTOMATED · BRANDED
          </div>
          <h1
            style={{
              fontSize: isMobile ? "2.2rem" : "3.2rem",
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              margin: "0 0 18px 0",
            }}
          >
            Stay top of mind with every past appraisal,
            <span style={{ color: BRAND_ACCENT_COLOR }}> on autopilot.</span>
          </h1>
          <p
            style={{
              fontSize: isMobile ? 16 : 18,
              color: "rgba(255,255,255,0.78)",
              lineHeight: 1.6,
              margin: "0 0 28px 0",
              maxWidth: 540,
            }}
          >
            Every month, every past appraisal gets a personalised value update,
            branded as you. When they open it or ask for an appraisal, you get
            pinged.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 28,
            }}
          >
            <button
              onClick={onStartTrial}
              style={{
                background: BRAND_ACCENT_COLOR,
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "14px 26px",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 12px 30px rgba(16,185,129,0.35)",
              }}
            >
              Start free trial →
            </button>
            <button
              onClick={onContact}
              style={{
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 10,
                padding: "14px 26px",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Book a demo
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: 12,
              maxWidth: 560,
            }}
          >
            {[
              { icon: "📅", label: "Sends every month" },
              { icon: "🎨", label: "Your branding & photo" },
              { icon: "🔔", label: "Live open + reply alerts" },
            ].map((f) => (
              <div
                key={f.label}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12.5,
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 16 }}>{f.icon}</span>
                {f.label}
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
              height: 0,
              width: 0,
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: -8 + (i - 3) * 30,
                  bottom: 0,
                  animation: `streamUp 4s linear ${i * 0.6}s infinite`,
                  opacity: 0.55,
                }}
              >
                <EnvelopeIcon size={22} />
              </div>
            ))}
          </div>
          <PhoneEmailScene isMobile={isMobile} />
        </div>
      </div>
    </section>
  );
}

/* ─────────── Stats band ─────────── */
function StatsBand({ isMobile }) {
  const stats = [
    { v: "12×", l: "branded touchpoints per year" },
    { v: "Monthly", l: "every customer, automatic" },
    { v: "0 min", l: "of agent time per send" },
    { v: "Live", l: "open & reply notifications" },
  ];
  return (
    <section
      style={{
        background: "#fff",
        borderTop: "1px solid #eef2f7",
        borderBottom: "1px solid #eef2f7",
        padding: isMobile ? "26px 20px" : "32px 40px",
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
          gap: isMobile ? 18 : 30,
        }}
      >
        {stats.map((s) => (
          <div key={s.l} style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: isMobile ? 26 : 36,
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: -1,
                lineHeight: 1,
              }}
            >
              {s.v}
            </div>
            <div
              style={{
                fontSize: 12,
                color: GRAY_DARK_COLOR,
                marginTop: 6,
                letterSpacing: 0.3,
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────── Flow section ─────────── */
function FlowSection({ isMobile }) {
  return (
    <section
      style={{
        background: `linear-gradient(135deg, ${BRAND_COLOR} 0%, #0f2540 100%)`,
        padding: isMobile ? "60px 20px" : "100px 40px",
        position: "relative",
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <SectionHeading
          kicker="THE FLOW"
          title="One automation. Twelve perfect touchpoints a year."
          sub="From the calendar tick to the reply in your inbox, every step is handled for you."
          light
          align="center"
        />
        <MonthlyFlow isMobile={isMobile} />
      </div>
    </section>
  );
}

/* ─────────── Audience section ─────────── */
function AudienceSection({ isMobile }) {
  return (
    <section
      style={{
        background: "#f8fafc",
        padding: isMobile ? "60px 20px" : "100px 40px",
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.05fr",
          gap: isMobile ? 40 : 60,
          alignItems: "center",
        }}
      >
        <div>
          <SectionHeading
            kicker="THE AUDIENCE"
            title="Every past appraisal, gently warmed forever."
            sub="Vendors don't sell because you knocked once. They sell when you stayed in their inbox while life happened."
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {[
              {
                t: "Past appraisals",
                b: "Anyone you've ever valued: imported once, nurtured forever.",
              },
              {
                t: "Buyers who lost out",
                b: "They'll buy again. Keep your name on their digital fridge.",
              },
              {
                t: "Old database leads",
                b: "Cold contacts are worth real money when reactivated with their address.",
              },
            ].map((x) => (
              <div
                key={x.t}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: "14px 16px",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: `${BRAND_ACCENT_COLOR}1f`,
                    color: BRAND_ACCENT_COLOR,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontWeight: 800,
                  }}
                >
                  ✓
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {x.t}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: GRAY_DARK_COLOR,
                      marginTop: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {x.b}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLOR} 0%, #0f2540 100%)`,
            borderRadius: 20,
            padding: isMobile ? "30px 20px" : "50px 30px",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.5,
              color: BRAND_ACCENT_COLOR,
              textAlign: "center",
              marginBottom: 14,
            }}
          >
            EVERY MONTH, EVERY CONTACT
          </div>
          <ContactOrbit isMobile={isMobile} />
          <div
            style={{
              textAlign: "center",
              marginTop: 16,
              fontSize: 13,
              color: "rgba(255,255,255,0.7)",
            }}
          >
            Your database, on a continuous loop of value.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Engagement section ─────────── */
function EngagementSection({ isMobile }) {
  return (
    <section
      style={{
        background: "#fff",
        padding: isMobile ? "60px 20px" : "100px 40px",
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
          gap: isMobile ? 40 : 60,
          alignItems: "center",
        }}
      >
        <div>
          <SectionHeading
            kicker="THE PING"
            title="The moment a vendor's ready, you know first."
            sub="Opens, clicks, comparable views, and 'request appraisal' taps, surfaced in real time so you can call while you're still front of mind."
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {[
              { v: "Opened", c: BRAND_BLUE },
              { v: "Clicked", c: BRAND_COLOR },
              { v: "Re-opened", c: "#f59e0b" },
              { v: "Appraisal request", c: BRAND_ACCENT_COLOR },
            ].map((x) => (
              <div
                key={x.v}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: x.c,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  {x.v}
                </span>
              </div>
            ))}
          </div>
        </div>
        <EngagementPanel isMobile={isMobile} />
      </div>
    </section>
  );
}

/* ─────────── FAQ ─────────── */
function FAQ({ isMobile }) {
  const items = [
    {
      q: "Where does the data come from?",
      a: "We pull live estimated values, comparable sales, and suburb trends from our own real-estate dataset: the same engine behind your homeprice profiles.",
    },
    {
      q: "Is this branded as me or as Vendorleads?",
      a: "Fully you. Your logo, your colour, your headshot, your phone number, your email. Replies go to your inbox.",
    },
    {
      q: "What if a contact unsubscribes?",
      a: "Standard one-click unsubscribe is built in. We respect it across all future sends automatically.",
    },
    {
      q: "Do I have to upload anything?",
      a: "No. Import your past appraisals once (CSV or paste). We handle every send from then on.",
    },
    {
      q: "How will I know when someone's hot?",
      a: "You get email + dashboard alerts when a contact opens, re-opens, or taps 'Request appraisal'. Heat-scored automatically.",
    },
  ];

  const [open, setOpen] = useState(0);

  return (
    <section
      style={{
        background: "#f8fafc",
        padding: isMobile ? "60px 20px" : "100px 40px",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <SectionHeading
          kicker="THE FINE PRINT"
          title="Common questions"
          align="center"
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {items.map((it, i) => {
            const isOpen = i === open;
            return (
              <button
                key={it.q}
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                style={{
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${isOpen ? BRAND_ACCENT_COLOR : "#e5e7eb"}`,
                  borderRadius: 12,
                  padding: "16px 18px",
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                  transition: "all 0.2s ease",
                  boxShadow: isOpen
                    ? "0 8px 24px rgba(16,185,129,0.12)"
                    : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {it.q}
                  </div>
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: isOpen ? BRAND_ACCENT_COLOR : "#f1f5f9",
                      color: isOpen ? "#fff" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 800,
                      flexShrink: 0,
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isOpen ? "−" : "+"}
                  </span>
                </div>
                {isOpen && (
                  <div
                    style={{
                      fontSize: 14,
                      color: GRAY_DARK_COLOR,
                      lineHeight: 1.65,
                      marginTop: 10,
                      animation: "fadeUp 0.3s ease both",
                    }}
                  >
                    {it.a}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────── CTA strip ─────────── */
function CTAStrip({ isMobile, onStartTrial, onContact }) {
  return (
    <section
      style={{
        background: `linear-gradient(135deg, ${BRAND_ACCENT_COLOR} 0%, #047857 100%)`,
        padding: isMobile ? "50px 20px" : "80px 40px",
        textAlign: "center",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -100,
          right: -50,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -100,
          left: -50,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
        }}
      />
      <div
        style={{
          position: "relative",
          maxWidth: 740,
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "1.8rem" : "2.6rem",
            fontWeight: 800,
            margin: 0,
            letterSpacing: -0.5,
            lineHeight: 1.15,
          }}
        >
          Set it up once. Stay top of mind forever.
        </h2>
        <p
          style={{
            fontSize: 16,
            opacity: 0.9,
            marginTop: 14,
            marginBottom: 28,
          }}
        >
          Free 14-day trial. No credit card. Cancel any time.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={onStartTrial}
            style={{
              background: "#fff",
              color: BRAND_ACCENT_COLOR,
              border: "none",
              borderRadius: 10,
              padding: "14px 26px",
              fontSize: 15,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 14px 30px rgba(0,0,0,0.2)",
            }}
          >
            Start free trial →
          </button>
          <button
            onClick={onContact}
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              borderRadius: 10,
              padding: "14px 26px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Talk to us
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Footer ─────────── */
function Footer({ isMobile }) {
  return (
    <footer
      style={{
        background: "#0b1220",
        color: "rgba(255,255,255,0.6)",
        padding: isMobile ? "40px 20px" : "60px 40px",
        fontSize: 13,
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: -0.3,
            }}
          >
            Vendorleads
          </div>
          <div style={{ marginTop: 4 }}>
            Lead generation, automated. © {new Date().getFullYear()}
          </div>
        </div>
        <div style={{ display: "flex", gap: 18 }}>
          <a
            href="/"
            style={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
            }}
          >
            Home
          </a>
          <a
            href="/features"
            style={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
            }}
          >
            Features
          </a>
          <a
            href="/pricing"
            style={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
            }}
          >
            Pricing
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ─────────── Page ─────────── */
function MonthlyAIPropertyReport() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );
  const [signInOpen, setSignInOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const openSignIn = () => setSignInOpen(true);
  const openContact = () => setContactOpen(true);

  return (
    <div
      style={{
        background: "#fff",
        minHeight: "100vh",
        fontFamily: "Inter, Arial, sans-serif",
      }}
    >
      <GlobalAnimations />
      <Navbar onSignIn={openSignIn} onContactClick={openContact} />
      <Hero
        isMobile={isMobile}
        onStartTrial={openSignIn}
        onContact={openContact}
      />
      <StatsBand isMobile={isMobile} />
      <FlowSection isMobile={isMobile} />
      <AudienceSection isMobile={isMobile} />
      <EngagementSection isMobile={isMobile} />
      <FAQ isMobile={isMobile} />
      <CTAStrip
        isMobile={isMobile}
        onStartTrial={openSignIn}
        onContact={openContact}
      />

      {signInOpen && (
        <SignInModal show={signInOpen} onHide={() => setSignInOpen(false)} />
      )}
      {contactOpen && (
        <ContactVendorleadsModal
          show={contactOpen}
          onHide={() => setContactOpen(false)}
        />
      )}
    </div>
  );
}

export default MonthlyAIPropertyReport;
